import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { checkoutAuthHeaders } from '../checkout/checkoutAuthHeaders';
import { hrefForSameSiteAsset } from '../productMediaUtils';
import { parseBespokePhotos } from '../jewelry/customerPhotoUpload';
import {
  bespokeCustomerStatusHint,
  bespokeCustomerStatusLabel,
  bespokeJewelryTypesLabel,
  bespokeOccasionLabel,
} from '../jewelry/bespokeFormOptions';
import { useSiteSettings } from '../SiteSettingsContext';

function CustomerBespokeDetail({ request, storeCopy }) {
  const dressPhotos = request.dress_photos?.length ? request.dress_photos : parseBespokePhotos(request.dress_photo);
  const referencePhotos = request.reference_photos?.length
    ? request.reference_photos
    : parseBespokePhotos(request.reference_photo);
  const statusHint = bespokeCustomerStatusHint(request.status);

  return (
    <div className="customer-bespoke-detail">
      {statusHint ? (
        <p className="customer-bespoke-detail__status-hint small text-muted mb-3">{statusHint}</p>
      ) : null}
      <dl className="customer-bespoke-detail__dl">
        <div>
          <dt>Occasion</dt>
          <dd>{bespokeOccasionLabel(request.occasion, storeCopy)}</dd>
        </div>
        <div>
          <dt>Jewelry</dt>
          <dd>{bespokeJewelryTypesLabel(request.jewelry_type, storeCopy)}</dd>
        </div>
        {request.event_date ? (
          <div>
            <dt>Event date</dt>
            <dd>{request.event_date}</dd>
          </div>
        ) : null}
        {request.notes ? (
          <div>
            <dt>Your notes</dt>
            <dd>{request.notes}</dd>
          </div>
        ) : null}
      </dl>
      {dressPhotos.length > 0 ? (
        <div className="customer-bespoke-detail__photos mb-3">
          <p className="small fw-semibold mb-2">Outfit photos</p>
          <div className="customer-bespoke-detail__photo-grid">
            {dressPhotos.map((url, index) => (
              <a
                key={`dress-${index}`}
                href={hrefForSameSiteAsset(url)}
                target="_blank"
                rel="noopener noreferrer"
                className="customer-bespoke-detail__photo"
              >
                <img src={hrefForSameSiteAsset(url)} alt={`Outfit ${index + 1}`} />
              </a>
            ))}
          </div>
        </div>
      ) : null}
      {referencePhotos.length > 0 ? (
        <div className="customer-bespoke-detail__photos">
          <p className="small fw-semibold mb-2">Reference photos</p>
          <div className="customer-bespoke-detail__photo-grid">
            {referencePhotos.map((url, index) => (
              <a
                key={`ref-${index}`}
                href={hrefForSameSiteAsset(url)}
                target="_blank"
                rel="noopener noreferrer"
                className="customer-bespoke-detail__photo"
              >
                <img src={hrefForSameSiteAsset(url)} alt={`Reference ${index + 1}`} />
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AccountCustomerBespokeRequests({ embedded = false }) {
  const { settings } = useSiteSettings();
  const storeCopy = settings?.store_copy;
  const [requests, setRequests] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const expandedRef = useRef(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/my_bespoke_requests.php', { headers: checkoutAuthHeaders() });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not load custom design requests.');
        setRequests([]);
        return;
      }
      setMessage(d.message || '');
      setRequests(Array.isArray(d.requests) ? d.requests : []);
    } catch {
      setError('Could not load custom design requests. If developing locally, run npm run php:serve in another terminal.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const toggleRequest = async (id) => {
    if (expandedRef.current === id) {
      expandedRef.current = null;
      setExpanded(null);
      setDetail(null);
      return;
    }
    expandedRef.current = id;
    setExpanded(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await fetch(`/api/my_bespoke_requests.php?id=${id}`, { headers: checkoutAuthHeaders() });
      const d = await r.json();
      if (expandedRef.current !== id) return;
      if (!r.ok || !d.success) {
        setDetail(null);
        setError(d.error || 'Could not load request details.');
        return;
      }
      setDetail(d.request);
    } catch {
      if (expandedRef.current === id) setDetail(null);
    } finally {
      if (expandedRef.current === id) setDetailLoading(false);
    }
  };

  const body = (
    <>
      {error ? (
        <div className="alert alert-danger py-2 small" role="alert">
          {error}
        </div>
      ) : null}
      {message && !loading && requests.length === 0 ? (
        <div className="alert alert-light border py-2 small mb-3" role="status">
          {message}
        </div>
      ) : null}
      {loading ? (
        <p className="small text-muted mb-0">Loading custom design requests…</p>
      ) : requests.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted mb-3">No custom jewelry requests yet — share your outfit photo and we will design around it.</p>
          <Link to="/custom-jewelry" className="btn btn-primary rounded-pill">
            Request custom design
          </Link>
        </div>
      ) : (
        <ul className="customer-bespoke-list list-unstyled mb-0">
          {requests.map((req) => {
            const active = expanded === req.id;
            return (
              <li key={req.id} className={`customer-bespoke-row${active ? ' customer-bespoke-row--open' : ''}`}>
                <button type="button" className="customer-bespoke-row__head" onClick={() => toggleRequest(req.id)}>
                  <span className="customer-bespoke-row__main">
                    <span className="customer-bespoke-row__number">#{req.request_number}</span>
                    <span className="customer-bespoke-row__meta">
                      {bespokeOccasionLabel(req.occasion, storeCopy)} · {bespokeJewelryTypesLabel(req.jewelry_type, storeCopy)}
                    </span>
                    <span className="customer-bespoke-row__when small text-muted">{formatWhen(req.created_at)}</span>
                  </span>
                  <span className={`customer-bespoke-row__status customer-bespoke-row__status--${req.status}`}>
                    {bespokeCustomerStatusLabel(req.status)}
                  </span>
                </button>
                {active ? (
                  <div className="customer-bespoke-row__detail">
                    {detailLoading ? (
                      <p className="small text-muted mb-0">Loading details…</p>
                    ) : detail ? (
                      <CustomerBespokeDetail request={detail} storeCopy={storeCopy} />
                    ) : (
                      <p className="small text-muted mb-0">Could not load details.</p>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {!loading && requests.length > 0 ? (
        <p className="small text-muted mt-3 mb-0">
          Need another piece?{' '}
          <Link to="/custom-jewelry">Submit a new custom design request</Link>.
        </p>
      ) : null}
    </>
  );

  if (embedded) return body;

  return (
    <section className="account-bespoke" aria-labelledby="account-bespoke-heading">
      <h2 id="account-bespoke-heading" className="account-section-label">
        Custom jewelry requests
      </h2>
      <div className="account-password-card">{body}</div>
    </section>
  );
}
